import React from 'react';
import { Info, TrendingUp, BarChart2, Briefcase, Shield, AlertTriangle, DollarSign } from 'lucide-react';

/**
 * Компонент с информацией об инвестициях в ценные бумаги в России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Инвестиции в ценные бумаги: руководство для начинающих
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-500" />
          Что такое инвестиции в ценные бумаги?
        </h3>
        <p>
          <strong>Инвестиции в ценные бумаги</strong> — это вложение средств в финансовые инструменты (акции, облигации, ETF, паи инвестиционных фондов и др.) с целью получения дохода в виде процентов, дивидендов или роста стоимости активов. Такие инвестиции позволяют защитить сбережения от инфляции и обеспечить рост капитала в долгосрочной перспективе.
        </p>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-indigo-500" />
          Основные виды ценных бумаг на российском рынке
        </h3>
        <ul>
          <li><strong>Акции</strong> — долевые ценные бумаги, которые дают право на владение частью компании и получение дивидендов.</li>
          <li><strong>Облигации</strong> — долговые ценные бумаги, по которым эмитент (государство или компания) обязуется выплатить проценты и вернуть основную сумму долга в установленный срок.</li>
          <li><strong>Государственные облигации (ОФЗ)</strong> — надежные ценные бумаги, выпускаемые Министерством финансов РФ.</li>
          <li><strong>Биржевые инвестиционные фонды (ETF, БПИФы)</strong> — фонды, акции которых торгуются на бирже и отражают стоимость базового индекса или корзины активов.</li>
          <li><strong>Паевые инвестиционные фонды (ПИФы)</strong> — коллективные инвестиции, управляемые профессиональными управляющими компаниями.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-indigo-500" />
          Структура инвестиционного портфеля
        </h3>
        <p>
          Правильно составленный инвестиционный портфель должен соответствовать вашим финансовым целям, горизонту инвестирования и отношению к риску. В России принято выделять следующие типы портфелей:
        </p>
        <ul>
          <li><strong>Консервативный портфель</strong> (низкий риск): преимущественно облигации (60-80%), небольшая доля акций (15-25%), денежные средства (5-15%).</li>
          <li><strong>Сбалансированный портфель</strong> (умеренный риск): акции (40-60%), облигации (30-50%), альтернативные инвестиции (5-10%).</li>
          <li><strong>Агрессивный портфель</strong> (высокий риск): преимущественно акции (70-90%), небольшая доля облигаций (5-20%), альтернативные инвестиции (5-10%).</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Преимущества диверсификации</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Диверсификация — распределение средств между различными активами для снижения рисков. Российским инвесторам рекомендуется диверсифицировать портфель не только между классами активов, но и между рынками (российский и зарубежный) и валютами для защиты от страновых и валютных рисков.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-500" />
          Особенности инвестирования в России
        </h3>
        <p>
          Инвестирование на российском рынке имеет свои особенности, которые важно учитывать при формировании инвестиционной стратегии:
        </p>
        <ul>
          <li><strong>Налогообложение</strong>: Доходы от инвестиций в России облагаются НДФЛ по ставке 13%. Дивиденды от российских компаний облагаются по ставке 13%, от иностранных — 15%.</li>
          <li><strong>Индивидуальный инвестиционный счет (ИИС)</strong>: Специальный брокерский счет, предоставляющий налоговые льготы при инвестировании на срок от 3 лет.</li>
          <li><strong>Тип А</strong>: Возврат 13% от суммы взносов (до 400 000 ₽ в год, максимальный вычет 52 000 ₽).</li>
          <li><strong>Тип Б</strong>: Освобождение от НДФЛ на все доходы, полученные на ИИС.</li>
          <li><strong>Система страхования инвестиций</strong>: С 2023 года в России действует система гарантирования средств на брокерских счетах, позволяющая вернуть до 1,4 млн рублей в случае банкротства брокера.</li>
          <li><strong>Валютные ограничения</strong>: Существуют определенные ограничения на операции с "недружественными" валютами, которые могут влиять на инвестиционную стратегию.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные риски при инвестировании
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li><strong>Рыночный риск</strong>: Возможность потери части инвестиций из-за общего падения рынка</li>
            <li><strong>Валютный риск</strong>: Изменение курса валют может негативно сказаться на доходности</li>
            <li><strong>Инфляционный риск</strong>: Рост цен может "съесть" часть доходности</li>
            <li><strong>Риск ликвидности</strong>: Сложность быстрой продажи актива без потери в цене</li>
            <li><strong>Страновой риск</strong>: Политические и экономические изменения в стране могут повлиять на инвестиции</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-500" />
          Как начать инвестировать в России
        </h3>
        <ol>
          <li><strong>Определите финансовые цели</strong>: Сформулируйте, для чего вы инвестируете, на какой срок и какую доходность хотите получить.</li>
          <li><strong>Создайте финансовую подушку</strong>: Перед началом инвестирования сформируйте резервный фонд, равный 3-6 месячным расходам.</li>
          <li><strong>Выберите брокера</strong>: Откройте брокерский счет у лицензированного российского брокера, обращая внимание на надежность, комиссии и функциональность платформы.</li>
          <li><strong>Рассмотрите открытие ИИС</strong>: Если вы гражданин РФ и платите НДФЛ, рассмотрите возможность открытия ИИС для получения налоговых льгот.</li>
          <li><strong>Начните с простых инструментов</strong>: Начинающим инвесторам рекомендуется начинать с ETF/БПИФ или консервативного портфеля с преобладанием облигаций.</li>
          <li><strong>Инвестируйте регулярно</strong>: Практикуйте стратегию усреднения (dollar-cost averaging), регулярно пополняя инвестиционный портфель независимо от состояния рынка.</li>
          <li><strong>Периодически пересматривайте портфель</strong>: Ребалансируйте портфель каждые 6-12 месяцев, возвращаясь к целевой структуре активов.</li>
        </ol>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
            <h4 className="text-indigo-700 dark:text-indigo-300 font-medium mb-2">Стратегия для начинающих</h4>
            <ul className="text-indigo-600 dark:text-indigo-400 text-sm">
              <li>Начните с консервативного портфеля</li>
              <li>Используйте ИИС для налоговых льгот</li>
              <li>Инвестируйте регулярно</li>
              <li>Диверсифицируйте между рублями и валютой</li>
              <li>Начните с ETF и ОФЗ</li>
            </ul>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
            <h4 className="text-green-700 dark:text-green-300 font-medium mb-2">Для долгосрочных целей</h4>
            <ul className="text-green-600 dark:text-green-400 text-sm">
              <li>Выбирайте широкую диверсификацию</li>
              <li>Используйте ИИС с льготами типа Б</li>
              <li>Увеличивайте долю акций</li>
              <li>Реинвестируйте дивиденды и купоны</li>
              <li>Минимизируйте комиссии и налоги</li>
            </ul>
          </div>
        </div>

        <h3>Популярные российские биржевые инструменты</h3>
        <ul>
          <li><strong>Фонды на индексы</strong>: БПИФ Тинькофф Индекс МОСБИРЖИ, БПИФ ВТБ Индекс МосБиржи, FXRU (российские корпоративные облигации)</li>
          <li><strong>Государственные облигации</strong>: ОФЗ 26207, ОФЗ 26215, ОФЗ 26223</li>
          <li><strong>Дивидендные акции</strong>: Сбербанк, Газпром, Лукойл, Норильский никель</li>
          <li><strong>ETF на зарубежные активы</strong>: ТИНБ (Тинькофф Вечный портфель), SBMX (S&P 500), VTBG (глобальные акции)</li>
        </ul>

        <h3>Заключение</h3>
        <p>
          Инвестиции в ценные бумаги — один из наиболее эффективных способов достижения долгосрочных финансовых целей и защиты сбережений от инфляции. Однако успешное инвестирование требует знаний, дисциплины и правильного подхода к управлению рисками.
        </p>
        <p>
          Помните, что инвестиции связаны с риском потери капитала, и прошлые результаты не гарантируют будущую доходность. Перед принятием инвестиционных решений рекомендуется получить консультацию профессионального финансового советника.
        </p>
        <p className="font-semibold mt-6">
          Наш калькулятор инвестиций поможет вам спланировать будущие инвестиции, оценить потенциальную доходность и выбрать оптимальную стратегию с учетом российской специфики!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;